import * as THREE from 'three';
import { Spell, SpellConfig } from './Spell';

export class IceShard extends Spell {
  private shards: THREE.Mesh[] = [];
  private frostTrail!: THREE.Points;
  private frostPositions!: Float32Array;
  private impactAnimationId: number | null = null;

  constructor(scene: THREE.Scene, startPos: THREE.Vector3, targetPos: THREE.Vector3, damage: number, isPlayerSpell: boolean = true) {
    const config: SpellConfig = {
      type: 'iceShard',
      damage,
      speed: 18,
      color: new THREE.Color(0x87CEEB),
      particleCount: 40,
      chargeTime: 1 // 1 second charge time for reaction
    };
    super(scene, startPos, targetPos, config, isPlayerSpell);
  }

  protected createSpellVisual(): void {
    // Initialize here because this is called from parent constructor before field initializers run
    this.shards = [];

    const mainShardGeometry = new THREE.ConeGeometry(0.15, 0.8, 4);
    const mainShardMaterial = new THREE.MeshBasicMaterial({
      color: 0x87CEEB,
      transparent: true,
      opacity: 0.9
    });
    const mainShard = new THREE.Mesh(mainShardGeometry, mainShardMaterial);
    mainShard.rotation.x = Math.PI / 2;

    const direction = new THREE.Vector3().subVectors(this.target, this.startPosition).normalize();
    mainShard.lookAt(direction);
    mainShard.rotateX(Math.PI / 2);

    this.mesh.add(mainShard);
    this.shards.push(mainShard);

    for (let i = 0; i < 4; i++) {
      const smallShardGeometry = new THREE.ConeGeometry(0.08, 0.4, 4);
      const smallShardMaterial = new THREE.MeshBasicMaterial({
        color: 0xADD8E6,
        transparent: true,
        opacity: 0.7
      });
      const smallShard = new THREE.Mesh(smallShardGeometry, smallShardMaterial);

      const angle = (i / 4) * Math.PI * 2;
      smallShard.position.set(
        Math.cos(angle) * 0.3,
        Math.sin(angle) * 0.3,
        -0.2
      );
      smallShard.rotation.x = Math.PI / 2 + (Math.random() - 0.5) * 0.3;
      smallShard.rotation.z = angle;

      this.mesh.add(smallShard);
      this.shards.push(smallShard);
    }

    const glowGeometry = new THREE.SphereGeometry(0.4, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x87CEEB,
      transparent: true,
      opacity: 0.3
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.mesh.add(glow);

    this.createFrostTrail();
  }

  private createFrostTrail(): void {
    const trailCount = 25;
    this.frostPositions = new Float32Array(trailCount * 3);

    for (let i = 0; i < trailCount * 3; i++) {
      this.frostPositions[i] = this.mesh.position.x;
    }

    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.frostPositions, 3));

    const trailMaterial = new THREE.PointsMaterial({
      color: 0xADD8E6,
      size: 0.15,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });

    this.frostTrail = new THREE.Points(trailGeometry, trailMaterial);
    this.scene.add(this.frostTrail);
  }

  protected createParticles(): THREE.Points {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.config.particleCount * 3);
    const colors = new Float32Array(this.config.particleCount * 3);

    for (let i = 0; i < this.config.particleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const r = 0.2 + Math.random() * 0.3;

      positions[i * 3] = r * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(theta);
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;

      colors[i * 3] = 0.7 + Math.random() * 0.3;
      colors[i * 3 + 1] = 0.9 + Math.random() * 0.1;
      colors[i * 3 + 2] = 1;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(geometry, material);
    this.mesh.add(particles);
    return particles;
  }

  protected updateParticles(delta: number): void {
    if (!this.particles || !this.particles.geometry.attributes.position) return;

    const positions = this.particles.geometry.attributes.position.array as Float32Array;

    for (let i = 0; i < this.config.particleCount; i++) {
      const angle = Date.now() * 0.005 + i * 0.2;
      const r = 0.2 + Math.sin(angle) * 0.1;

      positions[i * 3] = r * Math.cos(angle + i);
      positions[i * 3 + 1] = r * Math.sin(angle + i);
      positions[i * 3 + 2] += (Math.random() - 0.5) * 0.02;

      if (Math.abs(positions[i * 3 + 2]) > 0.3) {
        positions[i * 3 + 2] *= 0.9;
      }
    }

    this.particles.geometry.attributes.position.needsUpdate = true;

    if (this.shards) {
      this.shards.forEach((shard, index) => {
        if (index > 0 && shard) {
          shard.rotation.z += delta * 2;
        }
      });
    }

    if (this.frostTrail && this.frostPositions) {
      const trailCount = this.frostPositions.length / 3;
      for (let i = trailCount - 1; i > 0; i--) {
        this.frostPositions[i * 3] = this.frostPositions[(i - 1) * 3];
        this.frostPositions[i * 3 + 1] = this.frostPositions[(i - 1) * 3 + 1];
        this.frostPositions[i * 3 + 2] = this.frostPositions[(i - 1) * 3 + 2];
      }
      this.frostPositions[0] = this.mesh.position.x + (Math.random() - 0.5) * 0.2;
      this.frostPositions[1] = this.mesh.position.y + (Math.random() - 0.5) * 0.2;
      this.frostPositions[2] = this.mesh.position.z;

      this.frostTrail.geometry.attributes.position.needsUpdate = true;
    }
  }

  public createImpactEffect(): void {
    this.emitEvent('impact', { position: this.impactPosition.toArray(), type: 'iceShard' });

    const shatterParticleCount = 120;
    const positions = new Float32Array(shatterParticleCount * 3);
    const colors = new Float32Array(shatterParticleCount * 3);
    const velocities: THREE.Vector3[] = [];
    const impactPos = this.impactPosition;

    for (let i = 0; i < shatterParticleCount; i++) {
      positions[i * 3] = impactPos.x;
      positions[i * 3 + 1] = impactPos.y;
      positions[i * 3 + 2] = impactPos.z;

      // Color gradient: white core -> light blue -> deep blue
      const t = i / shatterParticleCount;
      if (t < 0.25) {
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.95; colors[i * 3 + 2] = 1;
      } else if (t < 0.6) {
        colors[i * 3] = 0.5 + Math.random() * 0.2;
        colors[i * 3 + 1] = 0.75 + Math.random() * 0.15;
        colors[i * 3 + 2] = 1;
      } else {
        colors[i * 3] = 0.2 + Math.random() * 0.2;
        colors[i * 3 + 1] = 0.4 + Math.random() * 0.2;
        colors[i * 3 + 2] = 0.9 + Math.random() * 0.1;
      }

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3 + Math.random() * 7;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.abs(Math.sin(phi) * Math.sin(theta)) * speed * 0.5 + 1,
        Math.cos(phi) * speed
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.18,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending
    });

    const shatter = new THREE.Points(geometry, material);
    this.scene.add(shatter);

    // Frost burst flash light
    const flashLight = new THREE.PointLight(0x88CCFF, 3, 10);
    flashLight.position.copy(impactPos);
    this.scene.add(flashLight);

    // Expanding ice ring
    const ringGeometry = new THREE.RingGeometry(0.1, 0.6, 6);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xADD8E6,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(impactPos);
    ring.lookAt(this.startPosition);
    this.scene.add(ring);

    // Second ring with offset timing
    const ring2Geometry = new THREE.RingGeometry(0.15, 0.5, 6);
    const ring2Material = new THREE.MeshBasicMaterial({
      color: 0x88CCFF,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    const ring2 = new THREE.Mesh(ring2Geometry, ring2Material);
    ring2.position.copy(impactPos);
    ring2.lookAt(this.startPosition);
    ring2.rotation.z = Math.PI / 6;
    this.scene.add(ring2);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = shatter.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < shatterParticleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 4 * 0.016; // gravity on shards
      }
      shatter.geometry.attributes.position.needsUpdate = true;

      ring.scale.setScalar(1 + elapsed * 6);
      ringMaterial.opacity = 0.8 * Math.max(0, 1 - (elapsed / 0.5));

      ring2.scale.setScalar(1 + Math.max(0, elapsed - 0.05) * 5);
      ring2Material.opacity = 0.6 * Math.max(0, 1 - ((elapsed - 0.05) / 0.45));

      material.opacity = Math.max(0, 1 - (elapsed / 0.8));
      flashLight.intensity = 3 * Math.max(0, 1 - (elapsed / 0.25));

      if (elapsed < 0.8) {
        this.impactAnimationId = requestAnimationFrame(animate);
      } else {
        this.impactAnimationId = null;
        this.scene.remove(shatter);
        this.scene.remove(ring);
        this.scene.remove(ring2);
        this.scene.remove(flashLight);
        geometry.dispose();
        material.dispose();
        ringGeometry.dispose();
        ringMaterial.dispose();
        ring2Geometry.dispose();
        ring2Material.dispose();
        flashLight.dispose();
      }
    };

    this.impactAnimationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.impactAnimationId !== null) {
      cancelAnimationFrame(this.impactAnimationId);
      this.impactAnimationId = null;
    }
    super.dispose();
    if (this.frostTrail) {
      this.scene.remove(this.frostTrail);
      this.frostTrail.geometry.dispose();
      (this.frostTrail.material as THREE.Material).dispose();
    }
  }
}
